import React, { useState, useCallback, useEffect } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import { useParams } from 'react-router-dom';
import UserProfile from '../index';
import {
  MyAssetItem,
  MyAssetsView,
  AssetRequestView,
  AssetRequestStatus,
  AssetRequestPriority,
  AssetCategoryView,
  AssetCondition,
  CreateAssetRequestInput,
  getMyAssets,
  getEmployeeAssets,
  getMyAssetRequests,
  createAssetRequest,
  getCategoriesForRequest,
} from '../../../api/General/assets';

interface Props {
  role: string;
  userId: string;
  selectedUser?: { id: string; fullName?: string };
  selectedUserManagers?: { id: string }[];
}

const conditionLabels: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "Excellent",
  [AssetCondition.Good]: "Good",
  [AssetCondition.Fair]: "Fair",
  [AssetCondition.Poor]: "Poor",
};

const conditionStyles: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "bg-emerald-100 text-emerald-800",
  [AssetCondition.Good]: "bg-blue-100 text-blue-800",
  [AssetCondition.Fair]: "bg-orange-100 text-orange-800",
  [AssetCondition.Poor]: "bg-red-100 text-red-800",
};

const requestStatusStyles: Record<AssetRequestStatus, string> = {
  [AssetRequestStatus.Pending]: "bg-yellow-100 text-yellow-800",
  [AssetRequestStatus.Approved]: "bg-green-100 text-green-800",
  [AssetRequestStatus.Rejected]: "bg-red-100 text-red-800",
};

const priorityStyles: Record<AssetRequestPriority, string> = {
  [AssetRequestPriority.Low]: "bg-blue-100 text-blue-800",
  [AssetRequestPriority.Medium]: "bg-orange-100 text-orange-800",
  [AssetRequestPriority.High]: "bg-red-100 text-red-800",
};

const ProfileAssets: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const { id: profileId } = useParams<{ id: string }>();
  const isOwnProfile = !profileId || profileId === userId;

  const [myAssets, setMyAssets] = useState<MyAssetsView | null>(null);
  const [requests, setRequests] = useState<AssetRequestView[]>([]);
  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<"current" | "history" | "requests">("current");

  // Request form
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestForm, setRequestForm] = useState<CreateAssetRequestInput>({
    categoryId: 0,
    reason: "",
    priority: AssetRequestPriority.Medium,
  });
  const [submitting, setSubmitting] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const targetId = profileId || userId;
      const assetsResult = isOwnProfile
        ? await getMyAssets()
        : await getEmployeeAssets(targetId);

      if (assetsResult.errors.length === 0) {
        setMyAssets(assetsResult.payload);
      }

      if (isOwnProfile) {
        const reqResult = await getMyAssetRequests();
        if (reqResult.errors.length === 0) {
          setRequests(reqResult.payload);
        }
      }

      const catResult = await getCategoriesForRequest();
      if (catResult.errors.length === 0) {
        setCategories(catResult.payload);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [profileId, userId, isOwnProfile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmitRequest = useCallback(async () => {
    if (!requestForm.categoryId || !requestForm.reason.trim()) {
      showToast(t("myAssets.fillRequiredFields"), "error");
      return;
    }

    setSubmitting(true);
    try {
      const result = await createAssetRequest(requestForm);
      if (result.errors.length === 0) {
        showToast(t("myAssets.requestSubmitted"), "success");
        setRequestModalOpen(false);
        setRequestForm({ categoryId: 0, reason: "", priority: AssetRequestPriority.Medium });
        loadData();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSubmitting(false);
    }
  }, [requestForm, loadData, showToast, t]);

  const renderAssetRow = (item: MyAssetItem, showReturn: boolean) => (
    <tr key={`${item.assetId}-${item.issueDate}`} className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.assetName}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.categoryName}</td>
      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.serialNumber || "—"}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{appDate(item.issueDate)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conditionStyles[item.condition]}`}>
          {t(`assets.condition${conditionLabels[item.condition]}`)}
        </span>
      </td>
      {showReturn && (
        <td className="px-4 py-3 text-sm text-gray-600">
          {item.returnDate ? appDate(item.returnDate) : "—"}
        </td>
      )}
    </tr>
  );

  return (
    <UserProfile>
      <div className="space-y-6">
        {/* Section Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveSection("current")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSection === "current"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid="my-assets-current-tab"
            >
              {t("myAssets.currentAssets")} ({myAssets?.current.length ?? 0})
            </button>
            <button
              onClick={() => setActiveSection("history")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSection === "history"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid="my-assets-history-tab"
            >
              {t("myAssets.assetHistory")} ({myAssets?.history.length ?? 0})
            </button>
            {isOwnProfile && (
              <button
                onClick={() => setActiveSection("requests")}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === "requests"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid="my-assets-requests-tab"
              >
                {t("myAssets.myRequests")} ({requests.length})
              </button>
            )}
          </div>
          {isOwnProfile && (
            <button
              onClick={() => setRequestModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              data-testid="request-asset-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("myAssets.requestAsset")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {/* Current Assets */}
            {activeSection === "current" && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {(myAssets?.current.length ?? 0) === 0 ? (
                  <div className="text-center py-12 px-4">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t("myAssets.noCurrentAssets")}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t("myAssets.noCurrentAssetsDescription")}</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.assetName")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.category")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.serialNumber")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.issuedDate")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.condition")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {myAssets?.current.map((item) => renderAssetRow(item, false))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* History */}
            {activeSection === "history" && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {(myAssets?.history.length ?? 0) === 0 ? (
                  <div className="text-center py-12 px-4">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t("myAssets.noHistory")}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t("myAssets.noHistoryDescription")}</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.assetName")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.category")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.serialNumber")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.issuedDate")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.condition")}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("myAssets.returnedDate")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {myAssets?.history.map((item) => renderAssetRow(item, true))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Requests */}
            {activeSection === "requests" && isOwnProfile && (
              <div className="space-y-3">
                {requests.length === 0 ? (
                  <div className="text-center py-12 px-4 bg-card border border-border rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t("myAssets.noRequests")}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t("myAssets.noRequestsDescription")}</p>
                  </div>
                ) : (
                  requests.map((req) => (
                    <div key={req.id} className="bg-card border border-border rounded-lg p-4" data-testid={`my-request-${req.id}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{req.categoryName}</h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${requestStatusStyles[req.status]}`}>
                          {req.status === AssetRequestStatus.Pending && t("assetRequests.pending")}
                          {req.status === AssetRequestStatus.Approved && t("assetRequests.approved")}
                          {req.status === AssetRequestStatus.Rejected && t("assetRequests.rejected")}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyles[req.priority]}`}>
                          {req.priority === AssetRequestPriority.Low && t("assetRequests.low")}
                          {req.priority === AssetRequestPriority.Medium && t("assetRequests.medium")}
                          {req.priority === AssetRequestPriority.High && t("assetRequests.high")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{req.reason}</p>
                      <p className="text-xs text-gray-400">
                        {t("myAssets.submitted")}: {appDate(parseApiUtc(req.createdOn))}
                      </p>
                      {req.reviewComment && (
                        <div className="mt-2 bg-gray-50 rounded-md p-2">
                          <p className="text-xs text-gray-500">{t("assetRequests.reviewComment")} ({req.reviewedByName}):</p>
                          <p className="text-sm text-gray-700">{req.reviewComment}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Request Asset Modal */}
        {requestModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{t("myAssets.requestAsset")}</h2>
                    <p className="text-sm text-gray-500">{t("myAssets.requestAssetDescription")}</p>
                  </div>
                </div>
                <button
                  onClick={() => setRequestModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-request-modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myAssets.assetCategory")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={requestForm.categoryId || ""}
                    onChange={(e) => setRequestForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    data-testid="request-category-select"
                  >
                    <option value="">{t("myAssets.selectCategory")}</option>
                    {/* The list is level-3 SKUs. Without the level-2 type an employee is choosing
                        between bare model names with nothing saying which is a laptop and which is
                        a monitor. */}
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.parentName ? `${cat.parentName} / ${cat.name}` : cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myAssets.priority")}
                  </label>
                  <select
                    value={requestForm.priority}
                    onChange={(e) => setRequestForm((prev) => ({ ...prev, priority: e.target.value as AssetRequestPriority }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    data-testid="request-priority-select"
                  >
                    <option value={AssetRequestPriority.Low}>{t("assetRequests.low")}</option>
                    <option value={AssetRequestPriority.Medium}>{t("assetRequests.medium")}</option>
                    <option value={AssetRequestPriority.High}>{t("assetRequests.high")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myAssets.reason")} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={requestForm.reason}
                    onChange={(e) => setRequestForm((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder={t("myAssets.reasonPlaceholder")}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                    data-testid="request-reason-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6 border-t border-border">
                <button
                  onClick={() => setRequestModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-card border border-border rounded-lg hover:bg-gray-50"
                  data-testid="cancel-request-btn"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleSubmitRequest}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="submit-request-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  {submitting ? t("myAssets.submitting") : t("myAssets.submitRequest")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-green-100 text-green-800 border border-green-200" : "bg-red-100 text-red-800 border border-red-200"
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </UserProfile>
  );
};

const mapStateToProps = (state: { user: { role: string; userId: string; selectedUser: { id: string; fullName?: string }; selectedUserManagers: { id: string }[] } }) => ({
  role: state.user.role,
  userId: state.user.userId,
  selectedUser: state.user.selectedUser,
  selectedUserManagers: state.user.selectedUserManagers,
});

export default connect(mapStateToProps)(ProfileAssets);
